// src/components/opportunities/opportunityPipeline.js
//
// Pure helpers for the Opportunities pipeline. Kept free of React so the rules
// that decide what colour a deal shows — the thing Craig actually reads the
// board by — are unit-testable without rendering anything.
//
// Pipeline status is LOCAL TO MSP OS. Nothing here is written back to Autotask.
// docTypes.js is React-free on purpose, so importing it here keeps this module
// renderable-without-React — see its header.
import { docTypeOf, DOC_LEAD } from "../proposals/docTypes";

export const OPEN        = "open";
export const CLOSED_WON  = "closed-won";
export const CLOSED_LOST = "closed-lost";

/* Linkage lives as a single `opportunityId` pointer on the Proposal/Quote,
   mirroring the existing `quote.proposalId`. The Opportunity deliberately holds
   no list of its own: a list on one side and a pointer on the other is two
   copies of one fact, and they drift. Everything below is computed. */

/* THE ONLY WAY TO READ AN OPPORTUNITY'S STATUS. Never touch `opp.status` or
   `opp.pipelineStatus` directly.

   Opportunities created before pipelineStatus existed carry the old
   `status: "open"` and nothing else — the previous UI could not set any other
   value ("For now, only open" was pinned in the markup), so every legacy record
   is genuinely open and falls through to OPEN. Reading `pipelineStatus` raw
   would therefore return undefined for those records.

   ⚠️ `opp.status` is DEAD. It is never written now and never updated on close,
   so it still reads "open" on a deal that was closed months ago. It is left in
   the stored data on purpose — deleting it would mean rewriting every blob to
   remove a field nothing reads — but a new dependency on it would be a bug that
   silently shows closed deals as open. */
export function pipelineStatusOf(opp) {
  const s = opp?.pipelineStatus;
  return s === CLOSED_WON || s === CLOSED_LOST ? s : OPEN;
}

export const isOpen = (opp) => pipelineStatusOf(opp) === OPEN;

/* The `!opportunityId` guard is load-bearing, not defensive noise. Without it a
   missing id makes the comparison `undefined === undefined`, which matches every
   proposal that was never linked to anything — so a broken card would claim
   every unlinked proposal in the system and read green off someone else's win. */
export function getLinkedProposals(opportunityId, allProposals = []) {
  if (!opportunityId) return [];
  return allProposals.filter((p) => p && p.opportunityId === opportunityId);
}

export function getLinkedQuotes(opportunityId, allQuotes = []) {
  if (!opportunityId) return [];
  return allQuotes.filter((q) => q && q.opportunityId === opportunityId);
}

/* Leads live in the SAME `proposals` array as real proposals — a Lead is a
   document type, not a separate subsystem — so every linked-document helper
   here returns both and callers that care must split them.

   Read the type through docTypeOf(), never `p.docType === "proposal"`: every
   record written before Leads shipped has no docType field at all, and a raw
   equality check would drop all of them. */
const isLeadDoc = (p) => docTypeOf(p) === DOC_LEAD;

/* Colour is DERIVED on every render, never stored. Storing it would mean every
   proposal status change had to remember to walk back and repaint its
   opportunity, and the one path that forgot would leave a stale green card. */
export function getOpportunityColour(opp, allProposals = [], allQuotes = []) {
  const linked          = getLinkedProposals(opp?.id, allProposals);
  const linkedQuotes    = getLinkedQuotes(opp?.id, allQuotes);
  const linkedProposals = linked.filter((p) => !isLeadDoc(p));
  const linkedLeads     = linked.filter(isLeadDoc);

  // Any single win turns the card green — a customer who signed one of three
  // quotes is a won deal, not a partially won one.
  const hasWon = linkedProposals.some((p) => p.status === "won")
              || linkedQuotes.some((q) => q.status === "won")
              || linkedLeads.some((l) => l.status === "won");
  if (hasWon) return "green";

  /* ⚠️ DELIBERATE ASYMMETRY, decided by Craig — do not "fix" this to match the
     line above it.

     A proposal or a quote counts at ANY status, draft included: writing one is
     already real work on a real deal. A LEAD only counts once it has been SENT.
     A Lead is cheap — it is the same document every time, seeded from a
     template in one click — so an unsent Lead draft is a bookmark, not
     progress, and letting it turn a card orange would mean the board could no
     longer distinguish "we have engaged this prospect" from "someone clicked
     + New Lead". "won" is included because a Lead can be marked won without
     ever passing through sent, and a won Lead is unambiguously sent. */
  const hasSentLead = linkedLeads.some((l) => l.status === "sent" || l.status === "won");

  if (linkedProposals.length || linkedQuotes.length || hasSentLead) return "orange";
  return "red";
}

/* Palette. `label` is what the card badge reads, so it says what the state MEANS
   rather than naming the colour. */
export const COLOUR_STYLE = {
  red:    { key: "red",    line: "var(--danger)", tint: "rgba(231,76,60,0.06)",  label: "Opportunity" },
  orange: { key: "orange", line: "var(--warn)",   tint: "rgba(243,156,18,0.07)", label: "In Progress" },
  green:  { key: "green",  line: "var(--ops)",    tint: "rgba(39,174,96,0.06)",  label: "Won" },
};

export const CLOSED_STYLE = {
  [CLOSED_WON]:  { line: "var(--ops)",   tint: "var(--s2)", label: "Closed · Won",  fg: "var(--ops)" },
  [CLOSED_LOST]: { line: "var(--text3)", tint: "var(--s2)", label: "Closed · Lost", fg: "var(--text3)" },
};

/* Counts shown on a card. Computed together so the card walks each list once.

   `proposals` counts REAL proposals only. Leads share the array but are a
   different, much lighter document — rolling them in would report "2 proposals"
   for a deal that has one proposal and one pre-proposal, which reads as twice
   as much work done as there is. */
export function opportunityCounts(opp, allProposals = [], allQuotes = []) {
  const linked = getLinkedProposals(opp?.id, allProposals);
  return {
    emails:    (opp?.emails || []).length,
    proposals: linked.filter((p) => !isLeadDoc(p)).length,
    leads:     linked.filter(isLeadDoc).length,
    quotes:    getLinkedQuotes(opp?.id, allQuotes).length,
  };
}

/* Which opportunities the board shows. Closed ones are hidden by default and,
   when revealed, lead with the most recently settled — a closed list is read as
   history, so the newest entry is the useful one. `closedAt` is missing on
   anything closed before it was recorded, and those sort last rather than
   jumping to the front on a 0. */
export function visibleOpportunities(opportunities = [], showClosed = false) {
  const list = opportunities.filter((o) => (showClosed ? !isOpen(o) : isOpen(o)));
  if (!showClosed) return list;   // open list keeps its existing newest-first order
  return [...list].sort((a, b) => (b.closedAt || 0) - (a.closedAt || 0));
}

/* Patch applied when closing. Kept here so both the detail view and any future
   caller close a deal the same way. */
export const closePatch = (won) => ({
  pipelineStatus: won ? CLOSED_WON : CLOSED_LOST,
  closedAt: Date.now(),
});

export const reopenPatch = () => ({ pipelineStatus: OPEN, closedAt: null });
